/**
 * Hook to load students for an entity, with async GraphQL support.
 */

import { useState, useEffect } from 'react';
import { getStudentsForEntity, fetchStudentsByEntity, getEntity, type Student, type Entity } from '@/lib/data';

const isMockData = import.meta.env.VITE_MOCK_DATA === 'true';

interface StudentsData {
  students: Student[];
  entity: Entity | undefined;
  loading: boolean;
  error: string | null;
}

export function useStudents(entityId: string | undefined): StudentsData {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const entity = entityId ? getEntity(entityId) : undefined;

  useEffect(() => {
    if (!entityId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isMockData) {
          setStudents(getStudentsForEntity(entityId));
        } else {
          const result = await fetchStudentsByEntity(entityId);
          setStudents(result);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load students');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entityId]);

  return { students, entity, loading, error };
}
